import { createAsyncThunk } from '@reduxjs/toolkit';

import { baixarPdfDoServidor } from '@/features/arbitral-detalhes';
import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';

import type {
  BaixarPdfCertificadoAcessosSentencaInput,
  CertificadoAcessosSentenca,
  FetchCertificadoAcessosSentencaInput,
} from '../../types/certificadoAcessosSentenca.types';
import {
  certificadoAcessosSentencaService,
  CertificadoSentencaSemPermissaoError,
  SentencaNaoProferidaError,
} from './services';
import { selectCertificadoAcessosSentencaPdfBaixando } from './selectors';

// Rejeições com semântica própria: SEM_PERMISSAO vira o estado "sem permissão" e NAO_PUBLICADA
// vira o estado Bloqueado (certificado nulo, sem erro) — nunca documento em branco.
export const CERTIFICADO_SENTENCA_SEM_PERMISSAO = 'SEM_PERMISSAO' as const;
export const CERTIFICADO_SENTENCA_NAO_PUBLICADA = 'NAO_PUBLICADA' as const;

export const fetchCertificadoAcessosSentenca = createAsyncThunk<
  CertificadoAcessosSentenca,
  FetchCertificadoAcessosSentencaInput,
  { rejectValue: string; state: RootState }
>('certificadoAcessosSentenca/fetch', async ({ origem, id }, { rejectWithValue }) => {
  try {
    return await certificadoAcessosSentencaService.getCertificado(origem, id);
  } catch (error) {
    if (error instanceof CertificadoSentencaSemPermissaoError) {
      return rejectWithValue(CERTIFICADO_SENTENCA_SEM_PERMISSAO);
    }
    if (error instanceof SentencaNaoProferidaError) {
      return rejectWithValue(CERTIFICADO_SENTENCA_NAO_PUBLICADA);
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Baixa o PDF emitido no servidor. Antes o botão rasterizava a tela no navegador, e o arquivo — que
 * nunca existiu no servidor — era recusado pelo próprio `/autenticar`, além de levar o código do
 * Documento da Sentença.
 */
export const baixarPdfCertificadoAcessosSentenca = createAsyncThunk<
  void,
  BaixarPdfCertificadoAcessosSentencaInput,
  { rejectValue: string; state: RootState }
>(
  'certificadoAcessosSentenca/baixarPdf',
  async ({ origem, id, nomeArquivo }, { rejectWithValue, signal }) => {
    try {
      await baixarPdfDoServidor(
        (sinal) => certificadoAcessosSentencaService.solicitarPdf(origem, id, sinal),
        nomeArquivo,
        signal,
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Falha ao gerar o PDF'));
    }
  },
  {
    // Um clique é uma solicitação: o duplo clique abriria uma segunda cadeia de sondagem.
    condition: (_arg, { getState }) => !selectCertificadoAcessosSentencaPdfBaixando(getState()),
  },
);
